import { Asset, Building, Cardinality, Checklist, Directionality, DocumentType, DocumentDetails, Entity, Invoice, Measurement, Order, Partnership, Query, Role, RoleAssignment, Site, Task, getEntityType, BusinessRule, BusinessRuleRuntimeStatus, Conversation, CloudVolume, KnowledgeBase, MarketplaceOffer, Resource } from "@the-real-insight/tri-model";
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";

const baseUrl = configuration.serverUrl;
let din276MetaData: any = null;

const handleResponse = async (response: any, callback: (entity: any) => void) => {
    if (response.ok) {
        const result = await response.json();

        if (result) {
            return callback(result);
        } else {
            return null;
        }
    } else {
        if (response.status === 400) {
            throw new ApplicationError((await response.json()).message);
        } else if (response?.status === 401) {
            throw new UnauthorizedError();
        }
    }
};

const handleResponseArray = async (response: any, callback: (entity: any) => void): Promise<any[]> => {
    const result = await response.json();

    if (result) {
        return result.map((entity: any) => callback(entity));
    } else {
        return [];
    }
};

const getResourceURL = (url: string) => {
    return `${baseUrl}${url}`;
}

const getFileURL = (customerID: string, url: string) => {
    return `${baseUrl}/${customerID}/files${url}`;
}

const getTheme = async (tenantId: string) => {
    const response = await fetch(`${baseUrl}/${tenantId}/theme`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        const result: any = await response.json();

        if (result.error) {
            throw new ApplicationError(result.error)
        } else {
            return result;
        }
    } else {
        if (response.status === 401) {
            throw new UnauthorizedError();
        }

        throw new Error();
    }
};

const getDIN276Metadata = async () => {
    if (din276MetaData) {
        return din276MetaData;
    }

    const response = await fetch(`${baseUrl}/metadata/din276`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.status === 200) {
        return din276MetaData = response.json();
    } else {
        throw new Error('Unspecified error.');
    }
};

const getPlans = async () => {
    const response = await fetch(`${baseUrl}/plans`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.status === 200) {
        return response.json();
    } else {
        throw new Error('Unspecified error.');
    }
};

const getCustomer = async (customerId: string) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getUsers = async () => {
    const response = await fetch(`${baseUrl}/users`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getBridges = async (query: any) => {
    const response = await fetch(`${baseUrl}/bridges`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(query), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getBuildings = async (query: any) => {
    const response = await fetch(`${baseUrl}/buildings`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(query),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const submitRequest = async (conversation: string, agent: string, profile: string, input: string, context: any) => {
    const response = await fetch(`${baseUrl}/submit`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify({
            conversation, agent, profile, input,
            context,
        }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });
    const data = await response.json();

    data.resources = data.resources ? data.resources.map((resource: any) => {
        resource.queries = (resource.queries || []).map((query: any) => Query.unmarshal(query));

        return resource;
    }) : undefined;

    return data;
};

const streamPromptResponse = (conversation: string, agent: string, profile: string, input: string, context: any, toolFilter: string[] = []) => {
    console.log('Tool filter >>>', toolFilter);

    return fetch(`${baseUrl}/skills/prompt/stream`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify({
            conversation, agent, profile, input,
            context,
            toolFilter,
        }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });
};

const triggerEvent = async (id: string) => {
    const response = await fetch(`${baseUrl}/admin/trigger`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ id }),
        headers: {
            'Content-Type': 'application/json',
            //"authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const checkEmail = async (email: string, firstName: string, lastName: string) => {
    const response = await fetch(`${baseUrl}/registration/checkEmail`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email, firstName, lastName }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.status === 200) {
        const result: any = await response.json();

        if (result.error) {
            throw new ApplicationError(result.error)
        } else {
            return result;
        }
    } else {
        throw 'Not authenticated.';
    }
};

const checkUser = async (username: string) => {
    const response = await fetch(`${baseUrl}/checkUser`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email: username }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        const result: any = await response.json();

        console.log(result);

        if (result.error) {
            throw new ApplicationError(result.error)
        } else {
            return result;
        }
    } else {
        if (response.status === 401) {
            throw new UnauthorizedError();
        }

        throw new Error();
    }
};


const validateEmail = async (email: string, validationCode: string) => {
    const response = await fetch(`${baseUrl}/registration/validateEmail`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email, validationCode }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        const result: any = await response.json();

        if (result.error) {
            throw new ApplicationError(result.error)
        } else {
            return result;
        }
    } else {
        throw Error('Server error.');
    }
};

const registerAndOrder = async (user: any, customer: any, legalPerson: any, plans: any, paymentMethod: any) => {
    console.log('Plans >>>', plans);
    const response = await fetch(`${baseUrl}/registration/registrationAndOrder`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ user, customer, legalPerson, plans, paymentMethod }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        return response.json();
    } else {
        if (response?.status === 401) {
            throw new UnauthorizedError();
        } else {
            throw Error('Server error.');
        }
    }
};

const login = async (email: string, password: string) => {
    const response = await fetch(`${baseUrl}/login`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email, password }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        const result: any = await response.json();

        if (result.error) {
            throw new ApplicationError(result.error)
        } else {
            return result;
        }
    } else {
        if (response.status === 401) {
            throw new UnauthorizedError();
        }

        throw new Error();
    }
};

const checkAuthentication = async () => {
    const response = await fetch(`${baseUrl}/authentication`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    console.log('Response status >>>', response.status);

    if (response.status === 200) {
        return true;
    } else {
        return false;
    }
};

const sendOTP = async () => {
    const response = await fetch(`${baseUrl}/otp`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.status === 200) {
        return response.json();
    } else {
        throw 'Not authenticated.';
    }
};

const checkOTP = async (email: string, otp: string) => {
    const response = await fetch(`${baseUrl}/otp`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        },
        body: JSON.stringify({ email, otp })
    });

    if (response.status === 200) {
        return response.json();
    } else {
        throw 'Not authenticated.';
    }
};

const updateUserCredentials = async (customerId: string, userId: string, oldPassword: string, newPassword: string, mobileNumber?: string) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}/users/${userId}`, {
        credentials: 'include',
        method: 'PATCH',
        body: JSON.stringify({ oldPassword, newPassword, mobileNumber }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        return response.json();
    } else {
        if (response.status === 400) {
            throw new ApplicationError((await response.json()).message);
        } else if (response?.status === 401) {
            throw new UnauthorizedError();
        } else {
            throw new Error('Failed to update credentials');
        }
    }
};

const logout = async () => {
    const response = await fetch(`${baseUrl}/logout`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getCustomerSettings = async (id: string) => {
    const response = await fetch(`${baseUrl}/customers/${id}/settings`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const sendEmail = async (mail: any) => {
    const response = await fetch(`${baseUrl}/email`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(mail),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const sendSMS = async (phoneNumber: string, message: string) => {
    const response = await fetch(`${baseUrl}/sms`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ phoneNumber, message }),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getMarkup = async (path: string) => {
    const response = await fetch(`${baseUrl}/markup/${path}`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.text();
}

const getBusinessRules = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/business-rules`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result.map((businessRule: BusinessRule) => new BusinessRule(businessRule));
    });

    return response.text();
}

const createBusinessRule = async (customerID: string, businessRule: BusinessRule) => {
    const response = await fetch(`${baseUrl}/${customerID}/business-rules`, {
        credentials: 'include',
        body: JSON.stringify(businessRule),
        method: 'POST', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result.map((businessRule: BusinessRule) => new BusinessRule(businessRule));
    });

    return response.text();
}

const updateBusinessRule = async (customerID: string, businessRule: BusinessRule) => {
    const response = await fetch(`${baseUrl}/${customerID}/business-rules/${businessRule._id}`, {
        credentials: 'include',
        body: JSON.stringify(businessRule),
        method: 'PUT', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result.map((businessRule: BusinessRule) => new BusinessRule(businessRule));
    });

    return response.text();
}

const activateBusinessRule = async (customerID: string, businessRule: BusinessRule) => {
    return updateBusinessRule(customerID, new BusinessRule({ ...businessRule, status: BusinessRuleRuntimeStatus.running }));
}

const deactivateBusinessRule = async (customerID: string, businessRule: BusinessRule) => {
    return updateBusinessRule(customerID, new BusinessRule({ ...businessRule, status: BusinessRuleRuntimeStatus.stopped }));
}

const deleteBusinessRule = async (customerID: string, businessRule: BusinessRule) => {
    const response = await fetch(`${baseUrl}/${customerID}/business-rules/${businessRule._id}`, {
        credentials: 'include',
        body: JSON.stringify(businessRule),
        method: 'DELETE', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result.map((businessRule: BusinessRule) => new BusinessRule(businessRule));
    });

    return response.text();
}

// >>> Move to Query

const bindInverseRelationships = (query: Query, rootEntity: any) => {
    return rootEntity;

    // let previousEntityTypeName = query.entityQuery.type.name;
    // let previousEntity = rootEntity;

    // query.linkedEntityQueries.forEach((linkedEntityQuery: LinkedEntityQuery) => {
    //     if (!linkedEntityQuery.relationship) {
    //         return;
    //     }

    //     logDebug('Retrieving relationship ', previousEntityTypeName, linkedEntityQuery.relationship);

    //     const linkedEntity = record.get(`${linkedEntityQuery.name || previousEntityTypeName /* hack */}`).properties;

    //     // Convert big numbers

    //     this.convertBigNums(linkedEntity);

    //     // @ts-ignore
    //     // logDebug('Linked entity ', linkedEntityQuery.type.name);
    //     logDebug(linkedEntity);
    //     // logDebug(previousEntityTypeName);
    //     // logDebug(previousEntity);

    //     let relationship;

    //     if (linkedEntityQuery.direction === RelationshipDirection.out) {
    //         relationship = getRelationship(previousEntityTypeName, linkedEntityQuery.relationship);

    //         // logDebug(relationship);
    //         // logDebug(`-[:${linkedEntityQuery.relationship}]->`);

    //         if (relationship.cardinality === Cardinality.ManyToMany ||
    //             relationship.cardinality === Cardinality.OneToMany) {
    //             previousEntity[relationship.name] = previousEntity[relationship.name] || [];

    //             previousEntity[relationship.name].push(linkedEntity)
    //         } else {
    //             previousEntity[relationship.name] = linkedEntity;
    //         }

    //         // We only set the forward references here. Setting both sides would lead to circular references during marshalling.
    //         // If need be, we can easily bind the reverse relationship on the client

    //         // if (relationship.directionality === Directionality.Bidirectional && relationship.inverseName) {
    //         //     if (relationship.cardinality === Cardinality.ManyToMany ||
    //         //         relationship.cardinality === Cardinality.ManyToOne) {
    //         //         linkedEntity[relationship.inverseName] = linkedEntity[relationship.name] || [];

    //         //         linkedEntity[relationship.inverseName].push(previousEntity)
    //         //     } else {
    //         //         linkedEntity[relationship.inverseName] = previousEntity;
    //         //     }
    //         // }
    //     } else {
    //         // TODO Ugly
    //         // @ts-ignore
    //         relationship = getRelationship(linkedEntityQuery.type.name, linkedEntityQuery.relationship);

    //         // logDebug(relationship);
    //         // logDebug(`<-[:${linkedEntityQuery.relationship}]-`);

    //         // We only set the forward references here. Setting both sides would lead to circular references during marshalling.
    //         // If need be, we can easily bind the reverse relationship on the client

    //         // if (relationship.cardinality === Cardinality.ManyToMany ||
    //         //     relationship.cardinality === Cardinality.OneToMany) {
    //         //     linkedEntity[relationship.name] = linkedEntity[relationship.name] || [];

    //         //     linkedEntity[relationship.name].push(previousEntity)
    //         // } else {
    //         //     linkedEntity[relationship.name] = previousEntity;
    //         // }

    //         if (relationship.directionality === Directionality.Bidirectional && relationship.inverseName) {
    //             if (relationship.cardinality === Cardinality.ManyToMany ||
    //                 relationship.cardinality === Cardinality.ManyToOne) {
    //                 previousEntity[relationship.inverseName] = previousEntity[relationship.inverseName] || [];

    //                 previousEntity[relationship.inverseName].push(linkedEntity)
    //             } else {
    //                 previousEntity[relationship.inverseName] = linkedEntity;
    //             }
    //         }
    //     }

    //     previousEntityTypeName = relationship.getTypeName();
    //     previousEntity = linkedEntity;
    // });
}

// <<<

const query = async (query: Query): Promise<any[]> => {
    const response = await fetch(`${baseUrl}/query`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(query.marshal()), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (query.fieldReferences && query.fieldReferences.length > 0) {
        console.log('response status >>>', response.status);

        return response.json();
    } else {
        const entityType: any = getEntityType(query.entityQuery.type.name);
        const json: any = await response.json();

        return json.map((properties: any) => bindInverseRelationships(query, new entityType(properties)));
    }
}

const queryOne = async (q: Query): Promise<any[] | null> => {
    const result = await query(q);

    if (!!result) {
        if (result.length > 1) {
            throw new Error('Query yields too few or too many entities.');
        }

        return result[0];
    } else {
        return null;
    }
}

// @deprecated

const queryGraph = async (query: Query): Promise<any[]> => {
    const response = await fetch(`${baseUrl}/graphs/query`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(query.marshal()), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (query.fieldReferences) {
        return response.json();
    } else {
        const entityType: any = getEntityType(query.entityQuery.type.name);

        return (await response.json() as any).map((properties: any) => new entityType(properties));
    }
}

//  @deprecated

const queryNoSQL = async (entityTypeName: string, filter: any): Promise<any[]> => {
    const response = await fetch(`${baseUrl}/nosql/query/${entityTypeName}`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(filter), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    const entityType: any = getEntityType(entityTypeName);
    const result = await response.json();

    console.log(entityType, result);

    return result.map((properties: any) => new entityType(properties));
}

const queryTelemetry = async (filter: any) => {
    const response = await fetch(`${baseUrl}/telemetry/query`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(filter), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return (await response.json() as any).map((properties: any) => new Measurement(properties));
}

const queryZip = async (query: Query): Promise<any> => {
    const response = await fetch(`${baseUrl}/query/zip`, {
        credentials: 'include',
        method: 'POST', body: JSON.stringify(query.marshal()), headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    const x = await response.blob();

    return x;
}

const getEntityByID = async (entityTypeName: string, id: string) => {
    const entityType: any = getEntityType(entityTypeName);
    const response = await fetch(`${baseUrl}/entities/${entityTypeName}/${id}`, {
        credentials: 'include',
        method: 'GET', headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    const properties = await response.json();

    console.log('Properties >>>', properties);

    const entity = new entityType(properties);

    console.log('Entity retrieved >>>', entity);

    return entity;
}

const getSkills = async () => {
    const response = await fetch(`${baseUrl}/skills`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
}

const getCityObjects = async (lat: number, lon: number, radius: number = 500) => {
    const response = await fetch(`${baseUrl}/city-objects?lat=${lat}&lon=${lon}&radius=${radius}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
}

const getCityObject = async (lat: number, lon: number) => {
    const response = await fetch(`${baseUrl}/city-objects/single?lat=${lat}&lon=${lon}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
}

const getDeviceTypeCatalog = async () => {
    const response = await fetch(`${baseUrl}/iot/device-types`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
}

const submitSupportRequest = async (email: string, request: string) => {
    const response = await fetch(`${baseUrl}/support/requests`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email, request }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getRoles = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/roles`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createRoleAssignment = async (customerID: string, user: string, role: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/role-assignments`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ user, role }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deleteRoleAssignment = async (customerID: string, roleAssignment: RoleAssignment) => {
    const response = await fetch(`${baseUrl}/${customerID}/role-assignments`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(roleAssignment),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createSite = async (customerID: string, site: Site) => {
    const response = await fetch(`${baseUrl}/${customerID}/sites`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(site),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const updateSite = async (customerID: string, site: Site) => {
    const response = await fetch(`${baseUrl}/${customerID}/sites`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(site),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deleteSite = async (customerID: string, site: Site) => {
    const response = await fetch(`${baseUrl}/${customerID}/sites`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(site),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createPartnership = async (customerID: string, partnership: Partnership) => {
    const response = await fetch(`${baseUrl}/${customerID}/partnerships`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(partnership),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const updatePartnership = async (customerID: string, partnership: Partnership) => {
    const response = await fetch(`${baseUrl}/${customerID}/partnerships`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(partnership),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deletePartnership = async (customerID: string, partnership: Partnership) => {
    const response = await fetch(`${baseUrl}/${customerID}/partnerships`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(partnership),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createRole = async (customerID: string, role: Role) => {
    const response = await fetch(`${baseUrl}/${customerID}/roles`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(role),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deleteRole = async (customerID: string, role: Role) => {
    const response = await fetch(`${baseUrl}/${customerID}/roles`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(role),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const updateAsset = async (asset: Asset) => {
    const response = await fetch(`${baseUrl}/assets`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(asset),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getProcessDefinitions = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/process-definitions`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getChecklists = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/checklists`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createChecklist = async (customerID: string, checklist: Checklist) => {
    const response = await fetch(`${baseUrl}/${customerID}/checklists`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(checklist),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const updateChecklist = async (customerID: string, checklist: Checklist) => {
    const response = await fetch(`${baseUrl}/${customerID}/checklists/${checklist._id}`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(checklist),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deleteChecklist = async (customerID: string, checklist: Checklist) => {
    const response = await fetch(`${baseUrl}/${customerID}/checklists/${checklist._id}`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(checklist),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getTasks = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return (await response.json()).map((task: any) => new Task(task));
};

const getOpenTasks = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/open`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return (await response.json()).map((task: any) => new Task(task));
};

const activateTask = async (customerID: string, task: Task) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/${task._id}/activate`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(task),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deactivateTask = async (customerID: string, task: Task) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/${task._id}/deactivate`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(task),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const deactivateAndSaveTask = async (customerID: string, task: Task) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/${task._id}/deactivate-and-save`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(task),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const completeTask = async (customerID: string, task: Task) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/${task._id}/complete`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(task),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const abortTask = async (customerID: string, task: Task) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/${task._id}/abort`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(task),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const taskStatistics = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/tasks/statistics`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const fullOrderForProject = async (customerId: string, projectID: string) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}/projects/${projectID}/service-orders`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    if (response.ok) {
        const result = await response.json();

        if (result) {
            return new Order(result);
        } else {
            return null;
        }
    } else {
        if (response.status === 400) {
            throw new ApplicationError((await response.json()).message);
        } else if (response?.status === 401) {
            throw new UnauthorizedError();
        }
    }
};

const getOrdersAsBuyer = async (customerId: string, from?: Date, to?: Date) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}/orders/buyer?from=${from?.toISOString()}&to=${to?.toISOString()}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponseArray(response, (entry: any) => {
        return new Order(entry);
    });
};

const getOrdersAsSeller = async (customerId: string, from?: Date, to?: Date) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}/orders/seller?from=${from?.toISOString()}&to=${to?.toISOString()}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponseArray(response, (entry: any) => {
        return new Order(entry);
    });
};

const getInvoiceForecastForProject = async (customerId: string, projectID: string, from: Date, to: Date) => {
    const response = await fetch(`${baseUrl}/customers/${customerId}/projects/${projectID}/invoices/forecast?from=${from.toISOString()}&to=${to.toISOString()}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponseArray(response, (entry: any) => {
        return new Invoice(entry);
    });
};

const runBillingForProject = async (customerID: string, projectID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/billing/projects/${projectID}`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({}),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const createMissingInvoicesForProject = async (customerID: string, projectID: string) => {
    const response = await fetch(`${baseUrl}/customers/${customerID}/projects/${projectID}/invoices`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({}),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const sampleDXF = async () => {
    const response = await fetch(`${baseUrl}/sample-dxf`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const uploadFile = async (form: any) => {
    const response = await fetch(`${baseUrl}/file`, {
        credentials: 'include',
        method: 'PUT',
        body: form,
        headers: {
            //'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const uploadDocument = async (customerID: string, file: File, title: string, documentType: DocumentType) => {
    const form = new FormData();

    form.append("file", file);

    const response = await fetch(`${baseUrl}/${customerID}/document-details/${title}/${documentType}`, {
        credentials: 'include',
        method: 'PUT',
        body: form,
        headers: {
            // 'Content-Type': 'application/x-www-form-urlencoded',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return new DocumentDetails(result);
    });
};

const deleteDocument = async (customerID: string, document: DocumentDetails) => {
    const response = await fetch(`${baseUrl}/${customerID}/document-details`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(document),
        headers: {
            'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return document;
    });
}

const classifyImageDIN276 = async (customerID: string, file: File) => {
    const form = new FormData();

    form.append("image", file);

    const response = await fetch(`${baseUrl}/${customerID}/image-classification/din276`, {
        credentials: 'include',
        method: 'POST',
        body: form,
        headers: {
            // 'Content-Type': 'application/x-www-form-urlencoded',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const assetPhotoDummy = async (customerID: string, file: File) => {
    const form = new FormData();

    form.append("assetPhoto", file);

    const response = await fetch(`${baseUrl}/assets/photo`, {
        credentials: 'include',
        method: 'PUT',
        body: form,
        headers: {
            // 'Content-Type': 'application/x-www-form-urlencoded',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const getConversations = async (customerID: string): Promise<Conversation[]> => {
    const response = await fetch(`${baseUrl}/${customerID}/conversations`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponseArray(response, (conversation: any) => {
        return new Conversation(conversation);
    });
};

const createConversation = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/conversations`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return handleResponse(response, (result: any) => {
        return new Conversation(result);
    });
};

const deleteConversation = async (customerID: string, conversation: Conversation) => {
    const response = await fetch(`${baseUrl}/${customerID}/conversations`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(conversation),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getStorage = async (customerID: string): Promise<CloudVolume> => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    const result = await handleResponseArray(response, (entry: any) => {
        return entry;
    });

    return new CloudVolume(result);
};

const addFolder = async (customerID: string, parentFolder: string, folder: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            action: 'addFolder',
            parentFolder,
            folder,
        })
    });
}

const deleteFolder = async (customerID: string, folder: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'DELETE',
    });

    return response.json();
}

const renameFolder = async (customerID: string, folder: string, newName: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify({
            action: 'renameFolder',
            folder,
            newName,
        }),
        headers: {
            'Content-Type': 'application/json',
        }
    });
    return response.json();
}

const downloadFile = async (customerID: string, path: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage/${path}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Accept': '*/*'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
    }

    // Get the content type from the response headers
    const contentType = response.headers.get('content-type');
    const contentDisposition = response.headers.get('content-disposition');

    // Get the blob directly from the response
    const blob = await response.blob();

    // Return both the blob and metadata
    return {
        blob,
        contentType,
        contentDisposition,
        filename: path.split('/').pop() || 'download'
    };
}

const uploadFileToFolder = async (customerID: string, folder: string, file: File) => {
    const form = new FormData();

    form.append("file", file);

    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'POST',
        body: form,
        headers: {
            'Content-Type': 'multipart/form-data',
        }
    });

    return response.json();
}

const renameFile = async (customerID: string, file: string, newName: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify({
            action: 'renameFile',
            file,
            newName,
        }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
}

const updateTags = async (customerID: string, file: string, tags: any) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify({
            action: 'updateTags',
            file,
            tags,
        }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
}

const deleteFile = async (customerID: string, file: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/storage`, {
        credentials: 'include',
        method: 'DELETE',
    });

    return response.json();
}

const getResource = async (customerID: string, resourceID: string): Promise<any> => {
    const response = await fetch(`${baseUrl}/${customerID}/resources/${resourceID}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (entry: any) => {
        return new Resource(entry);
    });
};

const createMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const updateMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    console.log('updateMarketplaceOffer', marketplaceOffer);

    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const deleteMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}`, {
        credentials: 'include',
        method: 'DELETE',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const submitMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/submit`, {
        credentials: 'include',
        method: 'PATCH',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        console.log('submitMarketplaceOffer', result);
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const publishMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/publish`, {
        credentials: 'include',
        method: 'PATCH',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const unpublishMarketplaceOffer = async (customerID: string, marketplaceOffer: MarketplaceOffer) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/unpublish`, {
        credentials: 'include',
        method: 'PATCH',
        body: JSON.stringify(marketplaceOffer),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const appealAfterOfferRejection = async (customerID: string, marketplaceOffer: MarketplaceOffer, appealReason: string) => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/appeal`, {
        credentials: 'include',
        method: 'PATCH',
        body: JSON.stringify({offer: marketplaceOffer, appealReason: appealReason}),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new (getEntityType(marketplaceOffer._type).type.type)(result);
    });
};

const updateKnowledgeBaseImages = async (customerID: string, knowledgeBase: KnowledgeBase, form: any) => {
    const response = await fetch(`${baseUrl}/${customerID}/knowledge-bases/${knowledgeBase._id}/images`, {
        credentials: 'include',
        method: 'PUT',
        body: form,
        headers: {
            //'Content-Type': 'application/json',
            // "authorization": "Bearer " + authentication.access_token
        }
    });

    return response.json();
};

const getMarketplaceOffers = async (customerID: string): Promise<any[]> => {
    const response = await fetch(`${baseUrl}/${customerID}/marketplace-offers`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return await handleResponseArray(response, (entry: any) => {
        return new (getEntityType(entry._type).type.type)(entry);
    });
};

const createServiceOrder = async (customerID: string, order: Partial<Order>) => {
    const response = await fetch(`${baseUrl}/${customerID}/orders`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(order),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return new Order(result as any);
    });
};

const getPaymentMethods = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-methods`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const addPaymentMethod = async (customerID: string, paymentMethodId: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-methods`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        const result = await response.json();
        return result;
    } else {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
    }
};

const removePaymentMethod = async (customerID: string, paymentMethodId: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-methods/${paymentMethodId}`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const setDefaultPaymentMethod = async (customerID: string, paymentMethodId: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-methods/default`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const getPaymentSummary = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-summary`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const getPaymentTransactions = async (customerID: string, limit: number = 10) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/payment-transactions?limit=${limit}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return handleResponse(response, (result: any) => {
        return result;
    });
};

const createStripeAccount = async (customerID: string, accountData: any) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/stripe-account`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify(accountData),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        const result = await response.json();
        return result;
    } else {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
    }
};

const getStripeAccount = async (customerID: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/stripe-account`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        const result = await response.json();
        return result;
    } else {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
    }
};

const updateStripeAccount = async (customerID: string, accountData: any) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/stripe-account`, {
        credentials: 'include',
        method: 'PUT',
        body: JSON.stringify(accountData),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        const result = await response.json();
        return result;
    } else {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
    }
};

const linkStripeAccount = async (customerID: string, accountId: string) => {
    const response = await fetch(`${baseUrl}/api/customers/${customerID}/stripe-account/link`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ accountId }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        const result = await response.json();
        return result;
    } else {
        const errorText = await response.text();
        throw new Error(`API call failed with status ${response.status}: ${errorText}`);
    }
};

// MFA Authenticator App setup
export const setupAuthenticatorApp = async () => {
    const response = await fetch(`${baseUrl}/api/mfa/auth-app/setup`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to start authenticator app setup');
    return response.json();
};

// MFA Authenticator App verify
export const verifyAuthenticatorApp = async (code: string, secret: string) => {
    const response = await fetch(`${baseUrl}/api/mfa/auth-app/verify`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, secret }),
    });
    if (!response.ok) throw new Error('Failed to verify authenticator app code');
    return response.json();
};

// Disable MFA
export const disableMFA = async () => {
    const response = await fetch(`${baseUrl}/api/mfa/disable`, {
        credentials: 'include',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to disable MFA');
    return response.json();
};

export const validateMFA = async (email: string, code: string) => {
    const response = await fetch(`${baseUrl}/api/mfa/validate`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email, code }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};

export const resetPassword = async (email: string) => {
    const response = await fetch(`${baseUrl}/reset-password`, {
        credentials: 'include',
        method: 'POST',
        body: JSON.stringify({ email }),
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (response.ok) {
        return response.json();
    } else {
        if (response.status === 400) {
            throw new ApplicationError((await response.json()).message);
        } else if (response?.status === 401) {
            throw new UnauthorizedError();
        } else {
            throw new Error('Failed to reset password');
        }
    }
};

const getAgenticServices = async () => {
    const response = await fetch(`${baseUrl}/administration/monitoring/agentic/tools`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

const getUsageMetrics = async () => {
    const response = await fetch(`${baseUrl}/administration/monitoring/metrics/usage`, {
        credentials: 'include',
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    return response.json();
};

export { updateUserCredentials, getAgenticServices };

export const userAPI = {
    getTheme,
    getDIN276Metadata,
    getPlans,
    getResourceURL,
    getFileURL,
    getCustomer,
    getUsers,
    getBridges,
    getBuildings,
    submitRequest,
    streamPromptResponse,
    triggerEvent,
    checkEmail,
    checkUser,
    validateEmail,
    registerAndOrder,
    login,
    checkAuthentication,
    sendOTP,
    checkOTP,
    updateUserCredentials,
    logout,
    getCustomerSettings,
    sendEmail,
    sendSMS,
    getMarkup,
    query,
    queryOne,
    queryGraph,
    queryNoSQL,
    queryTelemetry,
    queryZip,
    getSkills,
    getEntityByID,
    getCityObjects,
    getCityObject,
    getDeviceTypeCatalog,
    submitSupportRequest,
    createSite,
    updateSite,
    deleteSite,
    createPartnership,
    updatePartnership,
    deletePartnership,
    getRoles,
    createRole,
    deleteRole,
    createRoleAssignment,
    deleteRoleAssignment,
    updateAsset,
    getProcessDefinitions,
    getChecklists,
    createChecklist,
    updateChecklist,
    deleteChecklist,
    getTasks,
    getOpenTasks,
    activateTask,
    deactivateTask,
    deactivateAndSaveTask,
    completeTask,
    abortTask,
    taskStatistics,
    fullOrderForProject,
    getInvoiceForecastForProject,
    runBillingForProject,
    createMissingInvoicesForProject,
    getOrdersAsBuyer,
    getOrdersAsSeller,
    sampleDXF,
    uploadFile,
    uploadDocument,
    deleteDocument,
    classifyImageDIN276,
    assetPhotoDummy,
    getBusinessRules,
    createBusinessRule,
    updateBusinessRule,
    deactivateBusinessRule,
    activateBusinessRule,
    deleteBusinessRule,
    getConversations,
    createConversation,
    deleteConversation,
    getStorage,
    createMarketplaceOffer,
    updateMarketplaceOffer,
    deleteMarketplaceOffer,
    publishMarketplaceOffer,
    unpublishMarketplaceOffer,
    submitMarketplaceOffer,
    appealAfterOfferRejection,
    updateKnowledgeBaseImages,
    getMarketplaceOffers,
    createServiceOrder,
    getPaymentMethods,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    getPaymentSummary,
    getPaymentTransactions,
    createStripeAccount,
    getStripeAccount,
    updateStripeAccount,
    linkStripeAccount,
    addFolder,
    deleteFolder,
    renameFolder,
    downloadFile,
    uploadFileToFolder,
    renameFile,
    deleteFile,
    updateTags,
    getResource,
    resetPassword,
    getAgenticServices,
    getUsageMetrics,
}